import { getDocument } from '@atlaskit/browser-apis';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { MarkdownModePlugin, MarkdownModeView } from '../markdownModePluginType';

import { markdownModePluginKey } from './main';

const isMac =
	typeof navigator !== 'undefined' &&
	new RegExp('Mac', 'iu').test(
		(navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
			navigator.platform,
	);

/**
 * Returns true if the keyboard event matches the toggle shortcut:
 *   Mac:           Cmd + E   (metaKey)
 *   Windows/Linux: Ctrl + E  (ctrlKey)
 */
export const isToggleMarkdownViewShortcut = (e: KeyboardEvent): boolean => {
	const modifierMatch = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
	return modifierMatch && !e.altKey && !e.shiftKey && e.code === 'KeyE';
};

/**
 * Selector matching the wrapping container that holds both the ProseMirror
 * editor DOM and any plugin-rendered content (e.g. the markdown source view
 * mounted via `PluginSlot`). Defined in
 * `editor-core/src/ui/Appearance/FullPage/FullPageContentArea.tsx` as
 * `EDITOR_CONTAINER`.
 */
const EDITOR_CONTAINER_SELECTOR = '[data-editor-container="true"]';

/**
 * Returns true if this editor should respond to the toggle shortcut for the
 * current `document.activeElement`.
 *
 * Behaviour:
 *  - Focus inside this editor's ProseMirror DOM (rich-text mode) → true.
 *  - Focus inside this editor's container (the `[data-editor-container="true"]`
 *    ancestor of `editorView.dom`), e.g. on the CodeMirror surface in syntax
 *    mode or on the in-editor toggle button → true.
 *  - Focus inside a DIFFERENT editor's container on the same page → false.
 *    This preserves the multi-editor invariant: only the editor whose
 *    container holds the focus toggles.
 *  - Focus outside ANY editor container (e.g. on `document.body` after the
 *    user clicked the toolbar toggle button, or there is no `activeElement`)
 *    → true. The shortcut should still cycle the view in the common case
 *    where the editor temporarily loses focus.
 */
export const shouldHandleToggleShortcut = (editorView: EditorView): boolean => {
	const activeElement = getDocument()?.activeElement ?? null;

	// No active element / focus on body — fall through and allow toggle.
	// `document.body` is reported as `activeElement` in browsers when nothing
	// else is focused; treat that the same as "no focus".
	const hasMeaningfulFocus =
		activeElement !== null &&
		activeElement !== getDocument()?.body &&
		activeElement !== getDocument()?.documentElement;

	if (!hasMeaningfulFocus) {
		return true;
	}

	if (editorView.dom.contains(activeElement)) {
		return true;
	}

	const ourContainer =
		typeof editorView.dom.closest === 'function'
			? editorView.dom.closest(EDITOR_CONTAINER_SELECTOR)
			: null;

	if (ourContainer && ourContainer.contains(activeElement)) {
		return true;
	}

	// Multi-editor safety: if focus is inside SOME OTHER editor container,
	// only that editor should respond — not this one.
	const focusedContainer =
		typeof activeElement.closest === 'function'
			? (activeElement as Element).closest(EDITOR_CONTAINER_SELECTOR)
			: null;

	if (focusedContainer && focusedContainer !== ourContainer) {
		return false;
	}

	// Focus is outside any editor container (e.g. a popup, side panel, an
	// unrelated input). Allow the toggle so the shortcut works from "anywhere
	// on the page" — in particular right after clicking the view-mode toggle
	// button, which moves focus out of the editor.
	return true;
};

/**
 * Returns a document-level keydown handler that toggles the markdown view
 * between 'preview' and 'syntax'. This must be used instead of a ProseMirror
 * keymap because in syntax view the PM editor is hidden and unfocused, so PM
 * keymaps never fire.
 */
export const buildGlobalKeydownHandler = (
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined,
	getEditorView: () => EditorView | undefined,
): ((e: KeyboardEvent) => void) => {
	return (e: KeyboardEvent) => {
		if (!isToggleMarkdownViewShortcut(e)) {
			return;
		}
		const editorView = getEditorView();
		if (!editorView || editorView.isDestroyed) {
			return;
		}

		// Multi-editor guard: if focus is inside a DIFFERENT editor, only that
		// one should toggle. When focus is outside any editor (e.g. body, a
		// popup, a button) we still toggle so the shortcut works "anywhere on
		// the page", including immediately after the user clicked the toggle
		// button (which moves focus out of the editor).
		if (!shouldHandleToggleShortcut(editorView)) {
			return;
		}

		const pluginState = markdownModePluginKey.getState(editorView.state);

		if (!pluginState?.isMarkdownMode) {
			return;
		}

		const currentView: MarkdownModeView = pluginState.view ?? 'preview';

		// Only syntax and preview participate in the runtime toggle cycle.
		// Any other value is treated as non-toggleable.
		if (currentView !== 'syntax' && currentView !== 'preview') {
			return;
		}

		e.preventDefault();

		// Cycle through available views. When split view is enabled in the
		// future, add 'split-view' to this array.
		const viewCycle: MarkdownModeView[] = ['preview', 'syntax'];
		const currentIndex = viewCycle.indexOf(currentView);
		const nextView = viewCycle[(currentIndex + 1) % viewCycle.length];
		api?.markdownMode?.actions.setView(nextView);
	};
};
